import { Box, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Divider, EmphasizedNumber, Icons, MenuListItem } from 'utoniq-ui'

import { PrepaidPointBoxFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell } from '@/core/architecture'

type WalletHistoryItemProps = {
  transaction: PrepaidPointBoxFragment & { description?: string }
}

export enum TransactionType {
  GetCharge = 'get:charge',
  ConsumePurchase = 'consume:purchase',
  ConsumeGacha = 'consume:gacha',
  LoginBonus = 'get:login',
  ConsumeTip = 'consume:tip',
  MissionClear = 'get:missionReward',
  TransferGachaTIcket = 'get:transferGachaTicket',
}

type WalletHistoryItemIconProps = {
  type: string
}

export const WalletHistoryItem: Cell<WalletHistoryItemProps> = ({
  transaction: {
    formattedCreatedAt,
    type,
    formattedTransactionPrepaidPoint,
    description,
  },
}) => {
  const { t } = useTranslation('walletHistory')
  const { title, subTitle } = useMemo(() => {
    switch (type) {
      case TransactionType.GetCharge:
        return { title: t('pointCharge'), subTitle: formattedCreatedAt }
      case TransactionType.ConsumePurchase:
        return { title: t('itemPurchase'), subTitle: formattedCreatedAt }
      case TransactionType.ConsumeGacha:
        return { title: t('gachaDraw'), subTitle: formattedCreatedAt }
      case TransactionType.LoginBonus:
        return { title: t('loginBonus'), subTitle: formattedCreatedAt }
      case TransactionType.ConsumeTip:
        return { title: t('tip'), subTitle: formattedCreatedAt }
      case TransactionType.MissionClear:
        return {
          title: t('missionClear'),
          subTitle: (
            <Stack spacing={1} sx={{ minWidth: 0, mr: 8 }}>
              <Typography
                variant="overline"
                color="text.hint"
                sx={styleSets.lineCamp(1)}
              >
                {description}
              </Typography>
              <Typography variant="overline" color="text.hint">
                {formattedCreatedAt}
              </Typography>
            </Stack>
          ),
        }
      case TransactionType.TransferGachaTIcket:
        return {
          title: t('transferGachaTicket'),
          subTitle: formattedCreatedAt,
        }
      default:
        return { title: '', subTitle: formattedCreatedAt }
    }
  }, [t, type, formattedCreatedAt, description])

  return (
    <>
      <MenuListItem
        disableTouchRipple
        component={Box}
        sx={{
          px: 5,
          py: 5,
          height: 'auto',
          cursor: 'initial',
          '&:hover': { backgroundColor: 'transparent' },
        }}
        title={title}
        subTitle={subTitle}
        icon={<WalletHistoryItemIcon type={type} />}
        primaryTypographyProps={{
          variant: 'h6',
        }}
        secondaryTypographyProps={{
          variant: 'overline',
          color: 'text.hint',
        }}
        secondaryAction={
          parseFloat(formattedTransactionPrepaidPoint.replace(/,/gi, '')) >
          0 ? (
            <EmphasizedNumber variant="h5" color="primary.main">
              {formattedTransactionPrepaidPoint}
            </EmphasizedNumber>
          ) : (
            <EmphasizedNumber variant="h5" color="text.secondary">
              {formattedTransactionPrepaidPoint}
            </EmphasizedNumber>
          )
        }
      />
      <Divider sx={{ width: 'calc(100% - 40px)', ml: 5 }} />
    </>
  )
}

const WalletHistoryItemIcon = ({ type }: WalletHistoryItemIconProps) => {
  switch (type) {
    case TransactionType.ConsumePurchase:
      return <Icons.OwnedItems fontSize="large" />
    case TransactionType.ConsumeTip:
      return <Icons.Tip fontSize="large" />
    case TransactionType.ConsumeGacha:
    case TransactionType.GetCharge:
    case TransactionType.LoginBonus:
    case TransactionType.MissionClear:
      return <Icons.Point fontSize="large" />
    case TransactionType.TransferGachaTIcket:
      return <Icons.TicketToPoint fontSize="large" />
    default:
      return null
  }
}
